import React, { PureComponent, createRef } from 'react'
import { Document, Page } from 'react-pdf'
import { List } from 'react-virtualized'
import debounce from 'lodash.debounce'
import { Sentry } from '@helpers'

import Pagination from '../../Classroom/components/Frame/Pagination'
import { Zoom } from '../../Classroom/components/Frame/Tools'

const PAGE_GAP = 16
class PDFViewer extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      scale: 1,
      containerHeight: 0,
      containerWidth: 0,
      pdf: null,
      currentPage: 1,
      cachedPageDimensions: null,
      responsiveScale: 1,
      pageNumbers: new Map(),
      pages: new WeakMap()
    }

    this._list = createRef()

    this._callResizeHandler = debounce(this.handleResize, 50)
    this._callOrientationChangeHandler = debounce(this.handleResize, 1000)
  }

  componentDidMount() {
    this._mounted = true

    this.setState(this.computeConainerDimensions())

    window.addEventListener('resize', this._callResizeHandler)
    window.addEventListener(
      'orientationchange',
      this._callOrientationChangeHandler
    )
  }

  componentWillUnmount() {
    this._mounted = false
    window.removeEventListener('resize', this._callResizeHandler)
    window.removeEventListener(
      'orientationchange',
      this._callOrientationChangeHandler
    )
  }

  cachePageDimensions = (pdf) => {
    const promises = Array.from({ length: pdf.numPages }, (v, i) => i + 1).map(
      (pageNumber) => {
        return pdf.getPage(pageNumber)
      }
    )

    Promise.all(promises).then((pages) => {
      if (!this._mounted) {
        return
      }

      const pageDimensions = new Map()

      for (const page of pages) {
        const width = page.view[2] * this.state.scale
        const height = page.view[3] * this.state.scale

        pageDimensions.set(page.pageNumber, {
          width,
          height
        })
      }

      this.setState({ cachedPageDimensions: pageDimensions })
    })
  }

  recomputeRowHeights = () => {
    this._list.current.recomputeRowHeights()
  }

  computeRowHeight = ({ index }) => {
    const { cachedPageDimensions, responsiveScale, scale } = this.state

    console.log('computeRowHeight', cachedPageDimensions)
    if (cachedPageDimensions && responsiveScale) {
      return (
        (cachedPageDimensions.get(index + 1).height * scale) / responsiveScale +
        PAGE_GAP
      )
    }

    return 768 // Initial height
  }

  onDocumentLoadSuccess = (pdf) => {
    console.log('onDocumentLoadSuccess', pdf)
    this.setState({ pdf })
    this.cachePageDimensions(pdf)
  }

  updateCurrentVisiblePage = ({ startIndex }) => {
    this.setState({ currentPage: startIndex + 1 })
  }

  computeConainerDimensions = () => {
    if (!this.container)
      return {
        width: 0,
        height: 0
      }

    const { width, height } = this.container.getBoundingClientRect()
    return { containerHeight: height, containerWidth: width }
  }

  computeResponsiveScale = (pageNumber) => {
    const { cachedPageDimensions, pages, pageNumbers } = this.state

    const node = pages.get(pageNumbers.get(pageNumber))

    if (!node) return

    return cachedPageDimensions.get(pageNumber)[1] / node.clientHeight
  }

  handleResize = () => {
    const { currentPage, responsiveScale } = this.state

    const newResponsiveScale = this.computeResponsiveScale(currentPage)

    if (newResponsiveScale && responsiveScale !== newResponsiveScale) {
      this.setState({ responsiveScale: newResponsiveScale }, () =>
        this.recomputeRowHeights()
      )
    }

    this.setState(this.computeConainerDimensions())
  }

  zoomIn = () => {
    if (Math.round(this.state.scale * 100) / 100 >= 2.0) return
    this.setState({ scale: this.state.scale + 0.1 }, () => {
      this.recomputeRowHeights()
    })
  }

  zoomOut = () => {
    if (Math.round(this.state.scale * 100) / 100 <= 1.0) return
    this.setState({ scale: this.state.scale - 0.1 }, () => {
      this.recomputeRowHeights()
    })
  }

  render() {
    const { url } = this.props
    const {
      cachedPageDimensions,
      containerHeight,
      containerWidth,
      pdf,
      pages,
      pageNumbers,
      scale,
      currentPage
    } = this.state

    return (
      <div className='pdf-block'>
        <div
          className='document-wrapper'
          ref={(elem) => (this.container = elem)}
        >
          <Document
            file={url}
            onLoadSuccess={this.onDocumentLoadSuccess}
            onLoadError={(error) => Sentry.track(error)}
          >
            {cachedPageDimensions && (
              <List
                className='document'
                height={containerHeight}
                width={containerWidth}
                rowCount={pdf.numPages}
                rowHeight={this.computeRowHeight}
                overscanRowCount={5}
                onRowsRendered={this.updateCurrentVisiblePage}
                ref={this._list}
                rowRenderer={({ style, index }) => {
                  const numPages = pdf.numPages
                  const pageNumber = index + 1

                  return (
                    <div
                      style={style}
                      key={pageNumber}
                      className='document-page'
                    >
                      <div
                        ref={(ref) => {
                          if (!pageNumbers.has(pageNumber)) {
                            const key = { pageNumber }
                            pageNumbers.set(pageNumber, key)
                          }

                          pages.set(pageNumbers.get(pageNumber), ref)
                        }}
                      >
                        <Page
                          pageNumber={pageNumber}
                          scale={scale}
                          renderAnnotationLayer={false}
                          onLoadError={(error) => Sentry.track(error)}
                          onLoadSuccess={(page) => {
                            if (page.pageNumber === numPages) {
                              this.handleResize()
                            }
                          }}
                        />
                      </div>
                    </div>
                  )
                }}
              />
            )}
          </Document>
        </div>
        <Zoom onZoomIn={this.zoomIn} onZoomOut={this.zoomOut} />
        <Pagination ready={!!pdf} page={currentPage} total={pdf?.numPages} />
      </div>
    )
  }
}

export default PDFViewer
